import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import { AWARD_NAMES } from '../utils/certificates';
import { formatWindow } from '../utils/attendance';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared result message after adding people.
const report = (data) => alert(
  `${data.added} added${data.updated ? `, ${data.updated} already in the list (updated)` : ''}.` +
  (data.no_email ? `\n${data.no_email} without email — they can't be emailed.` : '')
);
const showError = (err) => {
  const d = err.response?.data;
  alert([d?.error || 'Failed to add', ...(d?.problems || [])].join('\n'));
};

export const AwardChoice = ({ value, onChange, extra }) => (
  <div className="form-group">
    <label>Certificates for everyone added</label>
    <select value={value} onChange={e => onChange(e.target.value)}>
      {extra}
      {Object.entries(AWARD_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  </div>
);

// Rows pasted from Excel or typed: one person per line, name and email in any
// order, optional employee ID; tabs or commas between them.
const parsePasted = (text) => text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  .map(line => line.split(/\t|,|;/).map(c => c.trim()).filter(Boolean))
  .filter(cells => !(cells.some(c => /^name$/i.test(c)) && cells.some(c => /^e-?mail/i.test(c)))) // header row
  .map(cells => {
    const email = cells.find(c => EMAIL_RE.test(c)) || '';
    const rest = cells.filter(c => c !== email);
    return { name: rest[0] || '', email, employee_id: rest[1] || '' };
  })
  .filter(p => p.name);

export const PasteModal = ({ batchId, onClose, onAdded }) => {
  const [text, setText] = useState('');
  const [award, setAward] = useState('attendance');
  const [saving, setSaving] = useState(false);
  const people = useMemo(() => parsePasted(text), [text]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/certificate-batches/${batchId}/people/bulk`, { people, award_mode: award });
      report(data);
      onAdded();
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header"><h3>Paste names</h3><button className="modal-close" onClick={onClose}>&times;</button></div>
        <div className="modal-body">
          <p className="text-sm text-muted">
            Copy the rows from Excel (Name, Email and optionally Employee ID) and paste them here, one person per line.
          </p>
          <div className="form-group">
            <textarea rows={8} value={text} onChange={e => setText(e.target.value)} autoFocus
              style={{ fontFamily: 'inherit' }} placeholder={'Shaik Feroz\tshaik.feroz@example.com\tMX1001\nAnita Rao\tanita.rao@example.com'} />
          </div>
          <AwardChoice value={award} onChange={setAward} />
          {people.length > 0 && (
            <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead><tr><th>Name</th><th>Email</th><th>Employee ID</th></tr></thead>
                <tbody>
                  {people.map((p, i) => (
                    <tr key={i}>
                      <td>{p.name}</td>
                      <td style={{ color: p.email ? undefined : '#b45309' }}>{p.email || 'no email'}</td>
                      <td>{p.employee_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !people.length}>
            {saving ? 'Adding...' : `Add ${people.length} ${people.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Copy trainees from a QR-attendance training (all its days) or from one day.
export const CopyFromAttendanceModal = ({ batchId, existingEmails, onClose, onAdded }) => {
  const [from, setFrom] = useState('training');
  const [trainings, setTrainings] = useState([]);
  const [days, setDays] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [people, setPeople] = useState([]);   // { key, name, email, employee_id, days_present?, days_total?, rule_award? }
  const [ruleKnown, setRuleKnown] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [fullOnly, setFullOnly] = useState(false);
  const [award, setAward] = useState('attendance');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/attendance/trainings').then(r => setTrainings(r.data)).catch(() => {});
    api.get('/attendance').then(r => setDays(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPeople([]); setSelected(new Set()); setRuleKnown(false);
    if (!sourceId) return;
    setLoading(true);
    const req = from === 'training'
      ? api.get(`/certificates/trainings/${sourceId}`).then(r => {
          const s = r.data.settings;
          setRuleKnown(!!(s.template_id || s.attendance_template_id || (s.quiz_ids || []).length));
          return r.data.trainees.map(t => ({
            key: t.employee_id, name: t.name, email: t.email || '', employee_id: t.employee_id,
            days_present: t.days_present, days_total: t.days_total,
            rule_award: t.override ? t.override.award : t.rule_award
          }));
        })
      : api.get(`/attendance/${sourceId}`).then(r => r.data.records.map(x => ({
          key: x.employee_id, name: x.name, email: x.email || '', employee_id: x.employee_id
        })));
    req.then(list => {
      setPeople(list);
      setSelected(new Set(list.map(p => p.key)));
    }).catch(() => alert('Failed to load that attendance'))
      .finally(() => setLoading(false));
  }, [from, sourceId]);

  useEffect(() => { if (award === 'rule' && !ruleKnown) setAward('attendance'); }, [ruleKnown, award]);

  const full = (p) => p.days_total === undefined || (p.days_total > 0 && p.days_present === p.days_total);
  const visible = people.filter(p => !fullOnly || full(p));
  const chosen = visible.filter(p => selected.has(p.key));
  const toggle = (k) => setSelected(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allTicked = visible.length > 0 && visible.every(p => selected.has(p.key));

  const save = async () => {
    setSaving(true);
    try {
      const payload = chosen.map(p => ({
        name: p.name, email: p.email, employee_id: p.employee_id,
        award: award === 'rule' ? p.rule_award : award
      }));
      const { data } = await api.post(`/certificate-batches/${batchId}/people/bulk`, { people: payload });
      report(data);
      onAdded();
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 760, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header"><h3>Copy from attendance</h3><button className="modal-close" onClick={onClose}>&times;</button></div>
        <div className="modal-body">
          <div className="flex gap-4 mb-3">
            <label className="flex flex-center gap-2"><input type="radio" checked={from === 'training'} onChange={() => { setFrom('training'); setSourceId(''); }} /> A training (all days)</label>
            <label className="flex flex-center gap-2"><input type="radio" checked={from === 'day'} onChange={() => { setFrom('day'); setSourceId(''); }} /> A single day</label>
          </div>
          <div className="form-group">
            <select value={sourceId} onChange={e => setSourceId(e.target.value)}>
              <option value="">Choose…</option>
              {from === 'training'
                ? trainings.map(t => <option key={t.id} value={t.id}>{t.title} ({t.trainee_count} trainees, {t.day_count} days)</option>)
                : days.map(d => <option key={d.id} value={d.id}>{d.training_id ? `${trainings.find(t => t.id === d.training_id)?.title || 'Training'} — ` : ''}{d.title} · {formatWindow(d.starts_at, d.ends_at)}</option>)}
            </select>
          </div>

          {loading && <p className="text-muted">Loading...</p>}
          {people.length > 0 && (
            <>
              <AwardChoice value={award} onChange={setAward}
                extra={from === 'training' && ruleKnown ? <option value="rule">As the training decides (attendance + score rule)</option> : null} />
              {from === 'training' && (
                <label className="flex flex-center gap-2 mb-2 text-sm">
                  <input type="checkbox" checked={fullOnly} onChange={e => setFullOnly(e.target.checked)} />
                  Only trainees present every day
                </label>
              )}
              <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                <table className="table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>
                        <input type="checkbox" checked={allTicked}
                          onChange={() => setSelected(s => { const n = new Set(s); visible.forEach(p => (allTicked ? n.delete(p.key) : n.add(p.key))); return n; })} />
                      </th>
                      <th>Name</th><th>Email</th>
                      {from === 'training' && <th>Days</th>}
                      {award === 'rule' && <th>Gets</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(p => (
                      <tr key={p.key}>
                        <td><input type="checkbox" checked={selected.has(p.key)} onChange={() => toggle(p.key)} /></td>
                        <td>{p.name}<div className="text-muted">{p.employee_id}</div></td>
                        <td>
                          {p.email || <span style={{ color: '#b45309' }}>no email</span>}
                          {p.email && existingEmails.has(p.email.toLowerCase()) && <div className="text-muted">already in list — will be updated</div>}
                        </td>
                        {from === 'training' && <td>{p.days_present}/{p.days_total}</td>}
                        {award === 'rule' && <td>{AWARD_NAMES[p.rule_award]}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-muted mt-2">You can edit names, emails and choices in the list afterwards.</p>
            </>
          )}
          {!loading && sourceId && people.length === 0 && <p className="text-muted">Nobody marked attendance there.</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !chosen.length}>
            {saving ? 'Copying...' : `Copy ${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
};
